/**
 * Aero Icon - A-Frame with Neural Node
 * Concept: A structural 'A' frame with a central neural node
 * Symbolizing Engineering + Intelligence
 */

interface AeroIconProps {
  className?: string;
  size?: number;
  variant?: 'solid' | 'outline' | 'animated';
}

export function AeroIcon({ className = "", size = 24, variant = 'outline' }: AeroIconProps) {
  if (variant === 'solid') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
      >
        {/* Main Triangle Frame - filled */}
        <path
          d="M12 2L2 22h20L12 2Z"
          fill="currentColor"
          opacity="0.15"
        />
        <path
          d="M12 2L2 22h20L12 2Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Internal Structure / Neural Connections */}
        <path d="M2 22L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M22 22L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        {/* Central Intelligence Node */}
        <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.3" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </svg>
    );
  }

  if (variant === 'animated') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
      >
        {/* Main Triangle Frame */}
        <path
          d="M12 2L2 22h20L12 2Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Internal Structure / Neural Connections */}
        <path d="M2 22L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M22 22L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        {/* Central Intelligence Node - Pulsing */}
        <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.2">
          <animate
            attributeName="r"
            values="2;3.5;2"
            dur="1.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.2;0.4;0.2"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="12" cy="12" r="1.5" fill="currentColor">
          <animate
            attributeName="r"
            values="1.5;2;1.5"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>
        {/* Energy pulse along connections */}
        <circle cx="12" cy="12" r="0.5" fill="currentColor">
          <animateMotion
            path="M0,0 L-10,10"
            dur="1s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="1;0"
            dur="1s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="12" cy="12" r="0.5" fill="currentColor">
          <animateMotion
            path="M0,0 L10,10"
            dur="1s"
            begin="0.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="1;0"
            dur="1s"
            begin="0.5s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    );
  }

  // Default: outline variant
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Main Triangle Frame */}
      <path
        d="M12 2L2 22h20L12 2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Internal Structure / Neural Connections */}
      <path d="M2 22L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M22 22L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Central Intelligence Node */}
      <circle cx="12" cy="12" r="2.5" fill="currentColor" opacity="0.2" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

/**
 * Small variant for navigation menu
 */
export function AeroNavIcon({ className = "" }: { className?: string }) {
  return <AeroIcon size={16} className={className} />;
}

/**
 * Large variant for welcome screen
 */
export function AeroLogoLarge({ className = "" }: { className?: string }) {
  return <AeroIcon size={64} variant="outline" className={className} />;
}

/**
 * Processing state variant
 */
export function AeroProcessing({ className = "" }: { className?: string }) {
  return <AeroIcon size={24} variant="animated" className={className} />;
}














